import { translationKey } from '../i18n';
import { localize, readAppLanguage } from '../i18n';
import useBackHandler from '../hooks/useBackHandler';
import { sheetSpacing } from '../design/tokens';
import SingleLineText from './ui/SingleLineText';
import LoadingLabel from './LoadingLabel';
import { isOral } from '../utils/intakeForm';
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import MedImage from './MedImage';
import useSheetDrag from '../hooks/useSheetDrag';

import { FONT_FAMILY as FONT_STACK } from '../design/tokens';

// PRN 톤 — 하늘색 카드 + 노랑 CTA (SnoozeSheet의 C 객체 구조와 동일)
const C = {
    cardBg: '#f0f9ff',
    cardBorder: '#bae6fd',
    accent: '#0369a1',
    bigText: '#0c4a6e',
    titleText: '#1F1D1B',
    subText: '#7A766F',
    primary: '#ffe566',
    primaryText: '#5a4a00',
    primaryActive: '#e8d040',
    chipBorder: '#EDE8DD',
    chipBg: '#fff',
    chipText: '#1F1D1B',
    cancelBorder: '#EDE8DD',
    cancelText: '#7A766F',
    handle: '#E2DDD2',
    danger: '#dc2626',
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toTimeStr = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const getToday = () => toDateStr(new Date());
const getMinDate = () => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toDateStr(d);
};
const doneVerb = (intakeForm) => isOral(intakeForm) ? translationKey('먹었어요') : translationKey('사용했어요');

// "오늘" / "어제" / "M월 D일 (요일)"
function formatDateLabel(dateStr) {
    if (!dateStr) return '';
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
    if (dateStr === toDateStr(today)) return localize("오늘");
    if (dateStr === toDateStr(yesterday)) return localize("어제");
    const d = new Date(`${dateStr}T00:00:00`);
    const dayName = [translationKey('일'), translationKey('월'), translationKey('화'), translationKey('수'), translationKey('목'), translationKey('금'), translationKey('토')][d.getDay()];
    return localize("{v0}월 {v1}일 ({v2})", { v0: d.getMonth() + 1, v1: d.getDate(), v2: localize(dayName) });
}

// "오후 7:30" 같은 표기
function formatTimeLabel(timeStr) {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    const ampm = localize(h < 12 ? '오전' : '오후');
    const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h);
    return `${ampm} ${h12}:${pad2(m)}`;
}

// 빠른 선택 — "현재시각 - N분"으로 계산해서 date+time 둘 다 설정
function buildPreset(minutesAgo) {
    const d = new Date();
    d.setMinutes(d.getMinutes() - minutesAgo);
    return { date: toDateStr(d), time: toTimeStr(d) };
}

function isFuture(dateStr, timeStr) {
    if (!dateStr || !timeStr) return false;
    const target = new Date(`${dateStr}T${timeStr}:00`);
    return target.getTime() > Date.now() + 30 * 1000; // 30초 마진
}

function openNativePicker(input) {
    if (!input) return;
    try {
        if (typeof input.showPicker === 'function') {
            input.showPicker();
            return;
        }
    } catch (_) {}
    try { input.focus(); } catch (_) {}
    try { input.click(); } catch (_) {}
}

/**
 * PRN 기록/수정 BottomSheet — SnoozeSheet / EditTakenTimeSheet 패턴과 통일.
 *
 * - 큰 날짜 박스 + 큰 시간 박스 (각각 박스 클릭 시 native picker, iOS WKWebView 호환)
 * - 빠른 선택 칩 4개 (지금 / 10분 전 / 30분 전 / 1시간 전)
 * - useSheetDrag 훅으로 핸들 드래그/탭으로 닫기, 본문 끌어내려도 닫기
 *
 * Props:
 * - open: boolean
 * - mode: 'create' | 'edit'
 * - medication: { name, imageUrl }
 * - initialDate, initialTime — edit 모드 기본값
 * - onClose, onConfirm({ date, time }), onDelete?
 */
export default function PrnLogSheet({
    open, mode = 'create', medication,
    initialDate, initialTime,
    onClose, onConfirm,
}) {
    const [date, setDate] = useState(initialDate || getToday());
    const [time, setTime] = useState(initialTime || toTimeStr(new Date()));
    // 어느 프리셋이 활성인지 (시각적 표시용). null이면 사용자 직접 선택.
    const [activePreset, setActivePreset] = useState('now');
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const submissionContext = useRef(0);
    const [visible, setVisible] = useState(false);
    const [animOpen, setAnimOpen] = useState(false);
    const dateInputRef = useRef(null);
    const timeInputRef = useRef(null);
    const lastOpenView = useRef({ mode, medication });
    if (open) lastOpenView.current = { mode, medication };
    const displayedMode = open ? mode : lastOpenView.current.mode;
    const displayedMedication = open ? medication : lastOpenView.current.medication;
    const titleId = 'prn-log-sheet-title';
    const requestClose = () => { if (!submittingRef.current) onClose(); };
    useBackHandler(open, requestClose, 1301);
    const { dragY, dragging, sheetHandlers, handleMouseHandlers } = useSheetDrag(requestClose);

    useLayoutEffect(() => {
        submissionContext.current++;
        submittingRef.current = false;
        if (!open) return () => { submissionContext.current++; };
        if (mode === 'edit' && initialDate && initialTime) {
            setDate(initialDate);
            setTime(initialTime);
            setActivePreset(null);
            setSubmitting(false);
        } else {
            const now = new Date();
            setDate(toDateStr(now));
            setTime(toTimeStr(now));
            setActivePreset('now');
            setSubmitting(false);
        }
        return () => { submissionContext.current++; };
    }, [open, mode, medication?.id, initialDate, initialTime]);

    useEffect(() => {
        if (open) {
            setVisible(true);
            const raf = requestAnimationFrame(() => setAnimOpen(true));
            return () => cancelAnimationFrame(raf);
        }
        setAnimOpen(false);
        const t = setTimeout(() => setVisible(false), 200);
        return () => clearTimeout(t);
    }, [open]);





    const applyPreset = (key) => {
        if (submittingRef.current) return;
        let preset;
        if (key === 'now') preset = { date: getToday(), time: toTimeStr(new Date()) };
        else if (key === '10m') preset = buildPreset(10);
        else if (key === '30m') preset = buildPreset(30);
        else if (key === '1h') preset = buildPreset(60);
        if (!preset) return;
        setDate(preset.date);
        setTime(preset.time);
        setActivePreset(key);
    };

    if (!visible) return null;

    const future = isFuture(date, time);
    const valid = !!date && !!time && !future && !submitting;
    const confirmLabel = displayedMode === 'edit' ? translationKey('변경') : translationKey('기록');
    const titleLabel = displayedMode === 'edit'
        ? (isOral(displayedMedication?.intakeForm) ? translationKey('복용 기록 수정') : translationKey('사용 기록 수정'))
        : translationKey('비상약 기록');

    const presets = [
        { key: 'now', label: translationKey('지금') },
        { key: '10m', label: translationKey('10분 전') },
        { key: '30m', label: translationKey('30분 전') },
        { key: '1h', label: translationKey('1시간 전') },
    ];

    const handleSubmit = async () => {
        if (!valid || submittingRef.current) return;
        submittingRef.current = true;
        const context = submissionContext.current;
        setSubmitting(true);
        try {
            await onConfirm?.({ date, time });
            if (context === submissionContext.current) onClose();
        } catch (_) {
            if (context === submissionContext.current) setSubmitting(false);
        } finally { if (context === submissionContext.current) submittingRef.current = false; }
    };

    return (
        <>
            {/* Backdrop */}
            <Box
                onClick={requestClose}
                data-tappable="true"
                sx={{
                    position: 'fixed', inset: 0, zIndex: 1300,
                    pointerEvents: open ? 'auto' : 'none',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    opacity: animOpen ? 1 : 0,
                    transition: 'opacity 150ms ease',
                }}
            />
            {/* Sheet */}
            <Box
                role="dialog" aria-modal="true" aria-labelledby={titleId}
                {...sheetHandlers}
                sx={{
                    position: 'fixed', bottom: 0, left: '50%',
                    pointerEvents: open ? 'auto' : 'none',
                    transform: animOpen
                        ? `translateX(-50%) translateY(${dragY}px)`
                        : 'translateX(-50%) translateY(100%)',
                    width: '100%', maxWidth: sheetSpacing.maxWidth, zIndex: 1301,
                    bgcolor: '#FFFFFF', borderRadius: '22px 22px 0 0',
                    px: sheetSpacing.horizontal, pt: '16px',
                    pb: sheetSpacing.bottom,
                    fontFamily: FONT_STACK,
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                    transition: dragging ? 'none' : 'transform 200ms ease-out',
                }}
            >
                {/* Handle — 드래그/탭으로 닫기 */}
                <Box
                    data-sheet-handle="true"
                    {...handleMouseHandlers}
                    sx={{
                        width: 40, height: 5, borderRadius: 2, bgcolor: C.handle,
                        mx: 'auto', mb: '12px', cursor: 'grab', touchAction: 'none',
                    }}
                />

                {/* Title — 약 이미지 + 이름 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: sheetSpacing.section, pl: '4px' }}>
                    <MedImage src={displayedMedication?.imageUrl} size={40} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SingleLineText sx={{ fontSize: '0.85rem', color: C.subText, fontFamily: FONT_STACK }}>
                            {localize(titleLabel)}
                        </SingleLineText>
                        <Typography
                            id={titleId}
                            sx={{
                                mt: '2px',
                                fontSize: '1.2rem', fontWeight: 800, letterSpacing: '-0.01em',
                                color: C.titleText, fontFamily: FONT_STACK,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                        >{displayedMedication?.name || localize("약")}</Typography>
                    </Box>
                </Box>

                {/* 날짜 디스플레이 박스 — 카드 전체가 native picker 터치 영역.
                    role="button"은 iOS WebKit에서 form control 클릭을 가로챌 수 있어 쓰지 않는다. */}
                <Box
                    onClick={() => openNativePicker(dateInputRef.current)}
                    sx={{
                        position: 'relative',
                        bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '14px',
                        py: '12px', px: '16px', mb: '8px',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            pointerEvents: 'none',
                        }}
                    >
                        <EventIcon sx={{ fontSize: 20, color: C.accent }} />
                        <Typography
                            tabIndex={0}
                            aria-label={localize("기록 날짜, {v0}, 변경하려면 활성화", { v0: formatDateLabel(date) })}
                            sx={{
                                fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em',
                                color: C.bigText, fontFamily: FONT_STACK, lineHeight: 1.1,
                                fontVariantNumeric: 'tabular-nums',
                                outline: 'none',
                                '&:focus-visible': { textDecoration: 'underline' },
                            }}
                        >{formatDateLabel(date)}</Typography>
                    </Box>
                    <SingleLineText sx={{
                        fontSize: '0.85rem', fontWeight: 700, color: C.accent,
                        fontFamily: FONT_STACK, flexShrink: 0, pointerEvents: 'none',
                    }}>{localize("날짜")}</SingleLineText>
                    <Box
                        component="input"
                        ref={dateInputRef}
                        disabled={submitting}
                        type="date"
                        value={date}
                        min={getMinDate()}
                        max={getToday()}
                        onClick={(e) => {
                            e.stopPropagation();
                            try {
                                if (typeof e.currentTarget.showPicker === 'function') {
                                    e.currentTarget.showPicker();
                                    return;
                                }
                            } catch (_) {}
                            try { e.currentTarget.focus(); } catch (_) {}
                        }}
                        onChange={(e) => {
                            if (!e.target.value) return;
                            setDate(e.target.value);
                            setActivePreset(null);
                        }}
                        aria-label={localize("기록 날짜 직접 선택")}
                        sx={{
                            position: 'absolute', inset: 0,
                            opacity: 0, cursor: 'pointer',
                            border: 'none', background: 'transparent',
                            fontSize: 16, color: 'transparent',
                            zIndex: 1,
                            '&::-webkit-calendar-picker-indicator': {
                                width: '100%', height: '100%', cursor: 'pointer',
                            },
                        }}
                    />
                </Box>

                {/* 시간 디스플레이 박스 — 카드 전체가 native picker 터치 영역 */}
                <Box
                    onClick={() => openNativePicker(timeInputRef.current)}
                    sx={{
                        position: 'relative',
                        bgcolor: C.cardBg, border: `1px solid ${C.cardBorder}`, borderRadius: '14px',
                        py: '14px', px: '16px',
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer',
                    }}>
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            pointerEvents: 'none',
                        }}
                    >
                        <AccessTimeIcon sx={{ fontSize: 20, color: C.accent }} />
                        <Typography
                            tabIndex={0}
                            aria-label={localize("기록 시각, {v0}, 변경하려면 활성화", { v0: time })}
                            sx={{
                                fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em',
                                color: C.bigText, fontVariantNumeric: 'tabular-nums',
                                fontFamily: FONT_STACK, lineHeight: 1,
                                outline: 'none',
                                '&:focus-visible': { textDecoration: 'underline' },
                            }}
                        >{time}</Typography>
                    </Box>
                    <SingleLineText sx={{
                        fontSize: '0.85rem', fontWeight: 700, color: C.accent,
                        fontFamily: FONT_STACK, flexShrink: 0, pointerEvents: 'none',
                    }}>{localize("시각")}</SingleLineText>
                    <Box
                        component="input"
                        ref={timeInputRef}
                        disabled={submitting}
                        type="time"
                        value={time}
                        onClick={(e) => {
                            e.stopPropagation();
                            try {
                                if (typeof e.currentTarget.showPicker === 'function') {
                                    e.currentTarget.showPicker();
                                    return;
                                }
                            } catch (_) {}
                            try { e.currentTarget.focus(); } catch (_) {}
                        }}
                        onChange={(e) => {
                            if (!e.target.value) return;
                            setTime(e.target.value);
                            setActivePreset(null);
                        }}
                        aria-label={localize("기록 시각 직접 선택")}
                        sx={{
                            position: 'absolute', inset: 0,
                            opacity: 0, cursor: 'pointer',
                            border: 'none', background: 'transparent',
                            fontSize: 16, color: 'transparent',
                            zIndex: 1,
                            '&::-webkit-calendar-picker-indicator': {
                                width: '100%', height: '100%', cursor: 'pointer',
                            },
                        }}
                    />
                </Box>

                {/* 선택 요약 / 미래 시각 경고 */}
                <Typography sx={{
                    fontSize: '0.9rem', color: future ? C.danger : C.subText,
                    fontFamily: FONT_STACK, fontWeight: future ? 700 : 500,
                    mt: '6px', mb: sheetSpacing.section, pl: '4px',
                    fontVariantNumeric: 'tabular-nums',
                }}>
                    {future
                        ? localize("미래 시각으로는 기록할 수 없어요")
                        : localize("{v0} {v1}에 {v2}", { v0: formatDateLabel(date), v1: formatTimeLabel(time), v2: localize(doneVerb(displayedMedication?.intakeForm)) })}
                </Typography>

                {/* 빠른 선택 — 4개 칩 한 줄 */}
                <SingleLineText sx={{
                    fontSize: '0.9rem', fontWeight: 700, color: C.subText,
                    fontFamily: FONT_STACK, mb: '6px', pl: '6px',
                }}>{localize("빠른 선택")}</SingleLineText>
                <Box sx={{ display: readAppLanguage() === 'ko' ? 'flex' : 'grid', ...(readAppLanguage() !== 'ko' && { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }), gap: '6px', flexWrap: 'nowrap', mb: sheetSpacing.action }}>
                    {presets.map(p => {
                        const isActive = activePreset === p.key;
                        return (
                            <Box
                                key={p.key}
                                component="button"
                                type="button"
                                disabled={submitting}
                                aria-pressed={isActive}
                                onClick={() => applyPreset(p.key)}
                                sx={{
                                    flex: 1, minWidth: 0,
                                    border: isActive ? '1px solid transparent' : `1px solid ${C.chipBorder}`,
                                    bgcolor: isActive ? C.primary : C.chipBg,
                                    color: isActive ? C.primaryText : C.chipText,
                                    fontSize: '0.95rem', fontWeight: 600, fontFamily: FONT_STACK,
                                    py: '8px', px: '6px', borderRadius: 999, cursor: 'pointer',
                                    fontVariantNumeric: 'tabular-nums',
                                    textAlign: 'center', whiteSpace: 'nowrap',
                                    '&:active': isActive ? { bgcolor: C.primaryActive } : { bgcolor: '#F8F5EE' },
                                }}
                            >{localize(p.label)}</Box>
                        );
                    })}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Box
                        component="button" type="button"
                        disabled={submitting}
                        onClick={requestClose}
                        sx={{
                            flex: 1,
                            border: `1px solid ${C.cancelBorder}`, bgcolor: '#fff', color: C.cancelText,
                            fontSize: '1rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: 0, borderRadius: '12px', cursor: 'pointer',
                            '&:active': { bgcolor: '#F8F5EE' },
                        }}
                    >{localize("취소")}</Box>
                    <Box
                        component="button" type="button"
                        disabled={!valid}
                        onClick={handleSubmit}
                        sx={{
                            flex: 2,
                            border: 'none',
                            bgcolor: valid ? C.primary : '#e5e7eb',
                            color: valid ? C.primaryText : '#999',
                            fontSize: '1.1rem', fontWeight: 700, fontFamily: FONT_STACK,
                            py: '13px', px: 0, borderRadius: '12px',
                            cursor: valid ? 'pointer' : 'not-allowed',
                            boxShadow: valid ? '0 2px 8px rgba(230,168,0,0.25)' : 'none',
                            '&:active': valid ? { bgcolor: C.primaryActive } : {},
                        }}
                    >{submitting ? <LoadingLabel /> : localize(confirmLabel)}</Box>
                </Box>
            </Box>
        </>
    );
}
